import React from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { StylesConfig, ValueType } from '@atlaskit/select';

import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { getColorPickerPalette } from '@atlassian/jira-servicedesk-customer-service-common';
import type { Field, SelectFieldOptionsType } from '../../types';
import { SelectFieldView } from '../common';
import { OptionView } from './option-view';
import { MultiSelectFieldReadView } from './read-view';

export const MultiSelectField = ({
	id,
	label,
	values,
	options,
	formattedOptions,
	onChange,
	isInline,
	getValueContainer,
}: Field) => {
	const { formatMessage } = useIntl();
	const palette = getColorPickerPalette(formatMessage);
	const mappedOptions: SelectFieldOptionsType[] =
		options?.map((value) => ({ value, label: value })) || [];

	const selectedOption: SelectFieldOptionsType[] = ff(
		'shipit-58-colours-for-detail-fields-in-csm_i6j6e',
	)
		? values
				?.map((value) => formattedOptions?.find((option) => option.value === value))
				.filter(Boolean) || []
		: mappedOptions.filter((option) => values?.includes(option.value));

	const onSelection = (
		selectedOptions: ValueType<SelectFieldOptionsType, true>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		const selectedValues = selectedOptions?.map((option) => option.value) || [];

		if (values?.join(',') === selectedValues.join(',')) return;

		onChange({ values: selectedValues }, analyticsEvent);
	};

	const selectViewStyles: StylesConfig = {
		multiValueLabel: (base, { data }) => ({
			...base,
			borderTopRightRadius: '0',
			borderBottomRightRadius: '0',
			backgroundColor: palette.find((c) => c.tag === data.color)?.value,
		}),
		multiValueRemove: (base, { data }) => ({
			...base,
			backgroundColor: data.color && palette.find((c) => c.tag === data.color)?.value,
		}),
	};

	return (
		<SelectFieldView
			isMulti
			id={id}
			label={label}
			closeMenuOnSelect={false}
			blurInputOnSelect={false}
			value={selectedOption}
			valueAsString={values?.join(', ')}
			options={
				ff('shipit-58-colours-for-detail-fields-in-csm_i6j6e') ? formattedOptions : mappedOptions
			}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			styles={ff('shipit-58-colours-for-detail-fields-in-csm_i6j6e') ? selectViewStyles : undefined}
			components={
				ff('shipit-58-colours-for-detail-fields-in-csm_i6j6e')
					? {
							Option: OptionView,
						}
					: undefined
			}
			onSelection={onSelection}
			isInline={isInline}
			readView={() => (
				<MultiSelectFieldReadView
					values={values}
					formattedOptions={formattedOptions}
					getValueContainer={getValueContainer}
					inInlineEdit
				/>
			)}
		/>
	);
};

export { MultiSelectFieldReadView };
